import React from 'react';
import { SparkIcon } from '@spark';
import { isAgenticAutonomyLevel } from './businessGoals/autonomyLevels';

/**
 * Wizard step that only exists for agentic tasks (Figma 3926-8787). Its icon
 * is the Figma "Agent" robot glyph, which is the Spark `robot` icon (the
 * `agent` slug is a different, unrelated glyph).
 */
export const AGENT_CONFIG_STEP = { key: 'agent-config', label: 'Agent Config', iconSlug: 'robot' };

/** Non-agentic default — Business Goals, Data Collection, Model Setup. */
export const WIZARD_STEPS = [
    { key: 'business-goals', label: 'Business Goals', iconSlug: 'business-goals' },
    { key: 'data-collection', label: 'Data Collection', iconSlug: 'data' },
    { key: 'model-setup', label: 'Model Setup', iconSlug: 'model' },
];

/**
 * Saved drafts and old links still carry `data-editor` and `train`. Both
 * land on Model Setup, which opens Review Training Plan.
 */
export const normalizeWizardStep = (key) =>
    key === 'data-editor' || key === 'train' ? 'model-setup' : key;

/** Every step the wizard can show, in order (agentic layout). */
export const ALL_WIZARD_STEPS = [WIZARD_STEPS[0], AGENT_CONFIG_STEP, ...WIZARD_STEPS.slice(1)];

/**
 * Steps for a task with the given autonomy level: agentic tasks (level ≠ 1)
 * get the "Agent Config" step between Business Goals and Data Collection.
 */
export const wizardStepsFor = (autonomyLevel) =>
    isAgenticAutonomyLevel(autonomyLevel) ? ALL_WIZARD_STEPS : WIZARD_STEPS;

export const wizardStepIndex = (key, steps = WIZARD_STEPS) => steps.findIndex((s) => s.key === key);

/**
 * Horizontal step header of the creation wizard (Business Goals →
 * [Agent Config →] Data Collection → Model Setup), per Figma 9846-17245.
 * Completed steps are clickable to navigate back.
 */
const WizardStepper = ({ activeStep, maxReachedStep, onStepClick, steps = WIZARD_STEPS }) => {
    const activeIdx = wizardStepIndex(activeStep, steps);
    const maxIdx = wizardStepIndex(maxReachedStep ?? activeStep, steps);

    return (
        <div className="cw-stepper" role="tablist" aria-label="Task creation steps">
            {steps.map((step, idx) => {
                const isActive = idx === activeIdx;
                const isCompleted = idx < activeIdx;
                const isReachable = idx <= maxIdx && !isActive;
                return (
                    <React.Fragment key={step.key}>
                        {idx > 0 && <span className="cw-stepper__connector" aria-hidden="true" />}
                        <button
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={[
                                'cw-stepper__step',
                                isActive ? 'cw-stepper__step--active' : '',
                                isCompleted ? 'cw-stepper__step--completed' : '',
                                isReachable ? 'cw-stepper__step--reachable' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onClick={isReachable && onStepClick ? () => onStepClick(step.key) : undefined}
                            disabled={!isActive && !isReachable}
                        >
                            <span className="cw-stepper__icon">
                                <SparkIcon slug={isCompleted ? 'check' : step.iconSlug} size="medium" />
                            </span>
                            <span className="cw-stepper__label">{step.label}</span>
                        </button>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default WizardStepper;
